import { TaskSectionNode } from "shared/api/task-hub/models";
import { FlatSection, TaskHubTreeRow } from "./types";

/** Total tasks anywhere under a section (including nested subsections). */
const countDescendantTasks = (section: TaskSectionNode): number =>
  section.tasks.length +
  section.subsections.reduce((sum, sub) => sum + countDescendantTasks(sub), 0);

/** Total subsections anywhere under a section (recursive). */
const countDescendantSections = (section: TaskSectionNode): number =>
  section.subsections.length +
  section.subsections.reduce(
    (sum, sub) => sum + countDescendantSections(sub),
    0
  );

/**
 * Convert the `section_hierarchy` payload into antd-compatible rows where
 * subsections and tasks are nested under `children`. Sections are listed
 * before tasks at every level.
 */
export const buildTreeRows = (root?: TaskSectionNode): TaskHubTreeRow[] => {
  if (!root) return [];

  const buildSection = (section: TaskSectionNode): TaskHubTreeRow[] => {
    const sectionRows: TaskHubTreeRow[] = section.subsections.map((sub) => ({
      rowType: "section",
      key: `section-${sub.id}`,
      id: sub.id as number,
      name: sub.name ?? "",
      parent_section: section.id,
      directTaskCount: sub.tasks.length,
      taskCount: countDescendantTasks(sub),
      sectionCount: countDescendantSections(sub),
      children: buildSection(sub),
    }));

    const taskRows: TaskHubTreeRow[] = section.tasks.map((task) => ({
      ...task,
      rowType: "task",
      key: `task-${task.id}`,
    }));

    return [...sectionRows, ...taskRows];
  };

  return buildSection(root);
};

/** Collect the row keys of every section row (used to expand the whole tree). */
export const collectSectionKeys = (rows: TaskHubTreeRow[]): string[] => {
  const acc: string[] = [];
  const walk = (items: TaskHubTreeRow[]) => {
    items.forEach((row) => {
      if (row.rowType === "section") {
        acc.push(row.key);
        walk(row.children);
      }
    });
  };
  walk(rows);
  return acc;
};

/** Flatten every section into a list (depth-first) for "move to" menus. */
export const flattenSections = (root?: TaskSectionNode): FlatSection[] => {
  if (!root) return [];

  const acc: FlatSection[] = [];
  const walk = (section: TaskSectionNode, depth: number) => {
    section.subsections.forEach((sub) => {
      acc.push({ id: sub.id as number, name: sub.name ?? "", depth });
      walk(sub, depth + 1);
    });
  };

  walk(root, 0);
  return acc;
};

/**
 * Collect a section id together with all of its descendant section ids. Used
 * to prevent moving a section into itself or one of its own descendants (which
 * would create a cycle).
 */
export const collectDescendantSectionIds = (
  root: TaskSectionNode | undefined,
  sectionId: number
): Set<number> => {
  const result = new Set<number>();
  if (!root) return result;

  const find = (section: TaskSectionNode): TaskSectionNode | null =>
    section.subsections.reduce<TaskSectionNode | null>((found, sub) => {
      if (found) return found;
      if (sub.id === sectionId) return sub;
      return find(sub);
    }, null);

  const target = find(root);
  if (!target) return result;

  const collect = (section: TaskSectionNode) => {
    result.add(section.id as number);
    section.subsections.forEach(collect);
  };
  collect(target);

  return result;
};

/**
 * Recursively filter the tree by a case-insensitive name query. A section is
 * kept if its own name matches or if any descendant task/section matches.
 */
export const filterTreeByName = (
  rows: TaskHubTreeRow[],
  query: string
): TaskHubTreeRow[] => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return rows;

  const filter = (items: TaskHubTreeRow[]): TaskHubTreeRow[] =>
    items.reduce<TaskHubTreeRow[]>((acc, row) => {
      const matchesSelf = row.name.toLowerCase().includes(normalized);

      if (row.rowType === "section") {
        const filteredChildren = filter(row.children);
        if (matchesSelf || filteredChildren.length > 0) {
          acc.push({ ...row, children: filteredChildren });
        }
      } else if (matchesSelf) {
        acc.push(row);
      }

      return acc;
    }, []);

  return filter(rows);
};
